import { useEffect, useRef, useState } from "react";

import { ApiError } from "../lib/api";

/**
 * Three states the UI has to render: loading, failed, loaded.
 *
 * No react-query. It is the right tool once there is cache invalidation,
 * refetch-on-focus and mutations to coordinate; this site reads eleven
 * endpoints that change only when Iftikhar edits content, and the server
 * already caches them for five minutes. Adding 13 kB and a provider to avoid
 * thirty lines is the wrong trade, and it is the kind of dependency an
 * interviewer reasonably asks you to justify.
 *
 * **Stale while revalidating.** When the dependencies change — the tech filter
 * on /projects — the previous results stay on screen until the new ones
 * arrive, instead of being replaced by "Loading…" and back again. That removes
 * a layout flash on every filter press, and it is also what keeps this hook
 * from calling setState synchronously inside an effect.
 */
export interface AsyncState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
}

export function useApi<T>(
  fetcher: () => Promise<T>,
  deps: readonly unknown[] = [],
): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    error: null,
    loading: true,
  });

  // Only the newest request may write state. Without this, a slow response for
  // "FastAPI" landing after a fast one for "Django" would leave the wrong list
  // on screen under the wrong filter.
  // A mutable holder rather than a bare counter, so the effect can capture it
  // once and the cleanup closes over that capture instead of re-reading
  // `ref.current` after the fact.
  const guard = useRef({ id: 0 });

  useEffect(() => {
    const tracker = guard.current;
    const request = ++tracker.id;

    void (async () => {
      try {
        const data = await fetcher();
        if (request === tracker.id)
          setState({ data, error: null, loading: false });
      } catch (cause: unknown) {
        if (request !== tracker.id) return;
        const error =
          cause instanceof ApiError
            ? cause
            : new ApiError(
                0,
                "unknown_error",
                "Something went wrong loading this.",
              );
        setState({ data: null, error, loading: false });
      }
    })();

    // Bumping the counter on cleanup invalidates any in-flight request, which
    // covers unmount and a dependency change with one mechanism rather than a
    // separate `cancelled` flag.
    return () => {
      tracker.id++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}
